<template>
  <div class="review-timeline">
    <!-- Controls + Tracks (single horizontal row, vertically centered) -->
    <div class="controls-row">
      <div class="timeline-controls">
        <!-- Play/Pause Button -->
        <button class="button is-small is-primary play-btn" @click="$emit('toggle-playback')">
          <span class="icon">
            <font-awesome-icon icon="fa-duotone fa-light fa-play" active-icon="fa-duotone fa-light fa-pause" :active="isPlaying" />
          </span>
        </button>

        <!-- Volume -->
        <div class="volume-control" @mouseenter="openVolume" @mouseleave="closeVolumeSoon">
          <button class="volume-btn" @click="onVolumeButtonClick">
            <span class="icon is-small">
              <!-- Multi-state mode (3 states): all variants pre-rendered,
                   visibility toggled by activeIndex. Avoids the CDN re-fetch
                   flicker / no-update bug of swapping :icon dynamically. -->
              <font-awesome-icon
                :icons="['fa-light fa-volume', 'fa-light fa-volume-low', 'fa-light fa-volume-slash']"
                :active-index="volumeIconIndex"
              />
            </span>
          </button>
          <div v-if="showVolume" class="volume-popover">
            <input
              type="range"
              class="volume-slider"
              min="0"
              max="1"
              step="0.05"
              :value="volume"
              @input="onVolumeInput"
            />
          </div>
        </div>

        <!-- Playback Speed -->
        <div class="speed-control" @mouseleave="showSpeed = false">
          <button class="speed-btn" :class="{ 'is-active': playbackRate !== 1 }" @click="showSpeed = !showSpeed" @mouseenter="showSpeed = true" :title="`Playback speed: ${playbackRate}x`">
            <span class="speed-label">{{ playbackRate }}x</span>
          </button>
          <div v-if="showSpeed" class="speed-popover">
            <button
              v-for="rate in speedOptions"
              :key="rate"
              class="speed-option"
              :class="{ 'is-selected': playbackRate === rate }"
              @click="$emit('update:playbackRate', rate); showSpeed = false"
            >
              {{ rate }}x
            </button>
          </div>
        </div>

        <!-- Comment Overlay Toggle (review context only) -->
        <button v-if="!hideCommentToggle" class="overlay-toggle-btn" :class="{ 'is-off': !showOverlay }" :title="showOverlay ? 'Hide comment overlay' : 'Show comment overlay'" @click="$emit('update:showOverlay', !showOverlay)">
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'message-slash']"
              :active-icon="['fal', 'message-lines']"
              :active="showOverlay"
            />
          </span>
        </button>

        <!-- Theater Mode (review context only) -->
        <button v-if="!hideTheaterToggle" class="overlay-toggle-btn" :class="{ 'is-off': !theaterMode }" :title="theaterMode ? 'Default view' : 'Theater mode'" @click="$emit('toggle-theater')">
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'rectangle-wide']"
              :active-icon="['fal', 'sidebar-flip']"
              :active="theaterMode"
            />
          </span>
        </button>

        <!-- Fullscreen -->
        <button class="overlay-toggle-btn" :class="{ 'is-off': !isFullscreen }" :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'" @click="$emit('toggle-fullscreen')">
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'expand']"
              :active-icon="['fal', 'compress']"
              :active="isFullscreen"
            />
          </span>
        </button>

        <!-- Extra context-specific buttons (e.g. heart/favorite, screenshot
             in AssetDetail). Slot is to the right of fullscreen so it sits
             at the visual end of the controls cluster. -->
        <slot name="extras" />
      </div>

      <!-- Time display sits between the controls cluster and the scrubber.
           The duration half only shows once a usable duration has resolved —
           the controls themselves never depend on it. -->
      <span class="time-display">
        {{ formatTime(currentTime) }}<template v-if="hasDuration"> / {{ formatTime(duration) }}</template>
      </span>

      <!-- Timeline Tracks — scrubbing needs a real duration, so this whole
           region waits for one. The controls above always render, so a video
           whose duration is slow/unresolved still gets a usable control bar
           (play/volume/speed/fullscreen) instead of the bar vanishing. -->
      <div class="timeline-tracks" v-if="hasDuration">
      <!-- Playback Track -->
      <div
        ref="trackRef"
        class="timeline-track"
        @pointerdown="onTrackPointerDown"
        @pointermove="onTrackPointerMove"
        @pointerup="endDrag"
        @pointercancel="endDrag"
        @pointerleave="hoverTime = null"
      >
        <!-- Progress Fill -->
        <div class="track-fill" :style="{ width: progressPercent + '%' }"></div>

        <!-- Playhead -->
        <div class="playhead" :style="{ left: progressPercent + '%' }"></div>

        <!-- Hover Tooltip -->
        <div
          v-if="hoverTime !== null"
          class="hover-tooltip"
          :style="{ left: hoverPercent + '%' }"
        >
          {{ formatTime(hoverTime) }}
        </div>
      </div>

      <!-- Feedback Track -->
      <div class="feedback-track" v-if="visibleComments.length">
        <div
          v-for="comment in visibleComments"
          :key="comment.id"
          class="comment-marker"
          :class="{
            'is-range': comment.timestampEnd != null,
            'is-resolved': comment.resolved,
            'is-active': comment.id === activeCommentId,
          }"
          :style="markerStyle(comment)"
          @mouseenter="hoveredComment = comment"
          @mouseleave="hoveredComment = null"
          @click.stop="$emit('comment-marker-click', comment)"
        >
          <span class="marker-dot" :style="{ background: comment.avatarColor }"></span>
        </div>

        <!-- Comment Marker Tooltip -->
        <div
          v-if="hoveredComment"
          class="marker-tooltip"
          :style="{ left: markerTooltipLeft }"
        >
          <div class="marker-tooltip-author">
            <span class="marker-tooltip-avatar" :style="{ background: hoveredComment.avatarColor }"></span>
            <strong>{{ hoveredComment.author }}</strong>
            <span class="marker-tooltip-time">{{ formatTime(hoveredComment.timestamp) }}</span>
          </div>
          <div class="marker-tooltip-text">{{ hoveredComment.text }}</div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { logger } from '@/services/loggerService'
import { formatTimestamp as formatTime } from '@/utils/reviewUtils'

const props = defineProps({
  currentTime: {
    type: Number,
    default: 0,
  },
  duration: {
    type: Number,
    default: 0,
  },
  comments: {
    type: Array,
    default: () => [],
  },
  isPlaying: {
    type: Boolean,
    default: false,
  },
  activeCommentId: {
    type: String,
    default: null,
  },
  volume: {
    type: Number,
    default: 1,
  },
  isMuted: {
    type: Boolean,
    default: false,
  },
  showOverlay: {
    type: Boolean,
    default: true,
  },
  isFullscreen: {
    type: Boolean,
    default: false,
  },
  theaterMode: {
    type: Boolean,
    default: false,
  },
  playbackRate: {
    type: Number,
    default: 1,
  },
  // Feature flags — let non-review contexts (AssetDetail) hide controls
  // that don't apply to them.
  hideCommentToggle: {
    type: Boolean,
    default: false,
  },
  hideTheaterToggle: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['seek', 'toggle-playback', 'comment-marker-click', 'update:volume', 'update:isMuted', 'update:showOverlay', 'update:playbackRate', 'toggle-theater', 'toggle-fullscreen'])

const trackRef = ref(null)
const hoverTime = ref(null)
const isDragging = ref(false)
const showVolume = ref(false)
// Close after a beat so a diagonal mouse path to the slider doesn't dismiss it
const VOLUME_CLOSE_DELAY = 250
let volumeCloseTimer = null
function openVolume() {
  clearTimeout(volumeCloseTimer)
  showVolume.value = true
}
function closeVolumeSoon() {
  clearTimeout(volumeCloseTimer)
  volumeCloseTimer = setTimeout(() => { showVolume.value = false }, VOLUME_CLOSE_DELAY)
}
const showSpeed = ref(false)
const hoveredComment = ref(null)
const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]

// Duration is only usable once it's a finite, positive number. Until then the
// bar still renders (controls are always available) but the track stays an empty
// rail and scrubbing no-ops, rather than dividing by 0/NaN/Infinity.
const hasDuration = computed(() => Number.isFinite(props.duration) && props.duration > 0)

const progressPercent = computed(() => {
  if (!hasDuration.value) return 0
  return (props.currentTime / props.duration) * 100
})

const hoverPercent = computed(() => {
  if (hoverTime.value === null || !hasDuration.value) return 0
  return (hoverTime.value / props.duration) * 100
})

const visibleComments = computed(() => {
  return props.comments.filter(c => c.timestamp != null)
})

const markerTooltipLeft = computed(() => {
  if (!hoveredComment.value || !hasDuration.value) return '0%'
  const pct = (hoveredComment.value.timestamp / props.duration) * 100
  return Math.max(5, Math.min(pct, 95)) + '%'
})

function markerStyle(comment) {
  if (!hasDuration.value) return { left: '0%' }
  const left = (comment.timestamp / props.duration) * 100
  if (comment.timestampEnd != null) {
    const width = ((comment.timestampEnd - comment.timestamp) / props.duration) * 100
    return { left: left + '%', width: Math.max(width, 0.5) + '%' }
  }
  return { left: left + '%' }
}

function getTimeFromEvent(event) {
  if (!trackRef.value || !hasDuration.value) return 0
  const rect = trackRef.value.getBoundingClientRect()
  if (!rect.width) return 0
  const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
  return (x / rect.width) * props.duration
}

let activePointerId = null
const hasLoggedDeadInteraction = ref(false)

// A user physically clicked/tapped the track and nothing could happen. The
// issue is unreproducible locally, so ship one report per mount through the
// error pipeline (errorLogService hooks console.error → Firestore, with the
// last-100-console-line breadcrumbs attached).
function logDeadInteraction(event) {
  if (hasLoggedDeadInteraction.value) return
  hasLoggedDeadInteraction.value = true
  logger.error('[ReviewTimeline] Timeline interaction swallowed', {
    duration: String(props.duration), // String() — JSON.stringify drops Infinity/NaN
    currentTime: props.currentTime,
    pointerType: event.pointerType || null,
    trackWidth: trackRef.value ? trackRef.value.getBoundingClientRect().width : null,
  })
}

function onTrackPointerDown(event) {
  if (event.button !== 0) return // primary button / primary touch only
  if (!hasDuration.value || !trackRef.value || !trackRef.value.getBoundingClientRect().width) {
    logDeadInteraction(event)
    return
  }
  isDragging.value = true
  activePointerId = event.pointerId
  hoverTime.value = null
  try {
    // Keep receiving move/up on the track even when the pointer leaves it —
    // replaces the old document-level mousemove/mouseup listeners.
    trackRef.value.setPointerCapture(event.pointerId)
  } catch {
    // capture unsupported/stale pointer — degrades to tap-to-seek
  }
  emit('seek', getTimeFromEvent(event))
}

function onTrackPointerMove(event) {
  if (isDragging.value) {
    if (event.pointerId !== activePointerId) return
    emit('seek', getTimeFromEvent(event))
    return
  }
  // Hover preview is a mouse-only affordance; touch/pen never hovers.
  if (event.pointerType === 'mouse') hoverTime.value = getTimeFromEvent(event)
}

function endDrag(event) {
  if (event.pointerId !== activePointerId) return
  isDragging.value = false
  activePointerId = null
  if (trackRef.value?.hasPointerCapture?.(event.pointerId)) {
    trackRef.value.releasePointerCapture(event.pointerId)
  }
}

onBeforeUnmount(() => {
  isDragging.value = false
  activePointerId = null
  clearTimeout(volumeCloseTimer)
})

// Index into the multi-state volume icon array:
//   0 = volume (full),  1 = volume-low (quiet),  2 = volume-slash (muted/zero).
// Order must match the :icons array on <font-awesome-icon>.
const volumeIconIndex = computed(() => {
  if (props.isMuted || props.volume === 0) return 2
  if (props.volume < 0.5) return 1
  return 0
})

function toggleMute() {
  emit('update:isMuted', !props.isMuted)
}

// Touch-aware volume button click. On desktop, mouseenter has already
// opened the popover so a click means "mute". On touch (no hover), the
// first tap needs to open the popover instead, otherwise the volume
// slider is unreachable. We discriminate by whether the popover is
// already open.
function onVolumeButtonClick() {
  if (!showVolume.value) {
    showVolume.value = true
    return
  }
  toggleMute()
}

function onVolumeInput(event) {
  const val = parseFloat(event.target.value)
  emit('update:volume', val)
  if (val > 0 && props.isMuted) {
    emit('update:isMuted', false)
  }
}

</script>

<style lang="scss" scoped>
.review-timeline {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0;
  user-select: none;
}

.controls-row {
  display: flex;
  align-items: center; // Vertically center buttons + time + scrubber.
  gap: 0.75rem;
}

.timeline-tracks {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.timeline-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

// Reach slotted content (e.g. AssetDetail's screenshot + heart buttons in
// the `extras` slot) so they pick up the same toolbar button shape without
// callers having to redeclare these rules.
.overlay-toggle-btn,
:slotted(.overlay-toggle-btn) {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--bulma-primary, #485fc7);
  padding: 2px;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--bulma-text);
  }

  &.is-off {
    color: var(--bulma-grey-light);
  }
}

.play-btn {
  width: 32px;
  height: 32px;
  border-radius: 50% !important;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-display {
  font-size: 0.75rem;
  color: var(--bulma-grey);
  font-variant-numeric: tabular-nums;
  // Tabular nums + a min-width keeps the time field from jiggling the
  // surrounding controls every second as digit widths change.
  min-width: 80px;
  flex-shrink: 0;
  text-align: center;
}

.volume-control {
  position: relative;
  display: flex;
  align-items: center;
}

.volume-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--bulma-grey);
  padding: 2px;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--bulma-text);
  }
}

.speed-control {
  position: relative;
  display: flex;
  align-items: center;
}

.speed-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--bulma-grey);
  padding: 2px 4px;
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 4px;

  &:hover {
    color: var(--bulma-text);
  }

  &.is-active {
    color: var(--bulma-primary, #485fc7);
  }
}

.speed-popover {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bulma-scheme-main, #fff);
  border: 1px solid var(--bulma-border, #eee);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 4px;
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  z-index: 10;
}

.speed-option {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 12px;
  font-size: 0.75rem;
  border-radius: 4px;
  white-space: nowrap;
  color: var(--bulma-text);
  text-align: center;

  &:hover {
    background: var(--bulma-grey-lightest, #f5f5f5);
  }

  &.is-selected {
    color: var(--bulma-primary, #485fc7);
    font-weight: 600;
  }
}

.volume-popover {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;

  // Invisible bridge over the 8px gap so moving up from the button stays "inside"
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: -8px;
    right: -8px;
    height: 10px;
  }
  background: var(--bulma-scheme-main, #fff);
  border: 1px solid var(--bulma-border, #eee);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.volume-slider {
  width: 4px;
  height: 80px;
  appearance: none;
  background: var(--bulma-grey-lighter, #eee);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  writing-mode: vertical-lr;
  direction: rtl;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--bulma-primary, #485fc7);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--bulma-primary, #485fc7);
    border: none;
    cursor: pointer;
  }
}

.timeline-track {
  height: 8px;
  background: var(--bulma-grey-lighter, #eee);
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  touch-action: none; // scrubbing must not become a page scroll/pinch

  // Invisible expanded hit area — the 8px rail is untappable on touch.
  // getTimeFromEvent only uses clientX, so the vertical slop is free.
  // Small bottom expansion keeps the comment markers below clickable.
  &::before {
    content: '';
    position: absolute;
    inset: -10px 0 -2px 0;
  }
}

.track-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: var(--bulma-primary, #485fc7);
  border-radius: 4px 0 0 4px;
  pointer-events: none;
}

.playhead {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--bulma-primary, #485fc7);
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;
}

.feedback-track {
  height: 10px;
  background: var(--bulma-grey-lightest, #f5f5f5);
  border-radius: 4px;
  position: relative;
}

.comment-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  cursor: pointer;
  padding: 2px;

  &.is-range {
    top: 0;
    height: 100%;
    border-radius: 3px;
    background: rgba(255, 193, 7, 0.35);
    transform: none;

    .marker-dot {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }

    &:hover {
      background: rgba(255, 193, 7, 0.55);
    }
  }

  &.is-resolved {
    opacity: 0.4;
  }

  &.is-active .marker-dot {
    transform: scale(1.4);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.15);
  }

  &.is-range.is-active .marker-dot {
    transform: translateY(-50%) scale(1.4);
  }

  .marker-dot {
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1.5px solid #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: transform 0.15s ease;
  }
}

.marker-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.88);
  color: #fff;
  font-size: 0.8rem;
  padding: 8px 12px;
  border-radius: 6px;
  width: max-content;
  max-width: 280px;
  pointer-events: none;
  z-index: 10;
  line-height: 1.4;

  .marker-tooltip-author {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .marker-tooltip-avatar {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .marker-tooltip-time {
    font-size: 0.7rem;
    opacity: 0.7;
    margin-left: auto;
    flex-shrink: 0;
  }

  .marker-tooltip-text {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    opacity: 0.9;
  }
}

.hover-tooltip {
  position: absolute;
  bottom: 100%;
  transform: translateX(-50%);
  margin-bottom: 6px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 4;
}

// ───────────────────────────────────────────────────────────────────────────
// Mobile (≤768px) — touch-friendly redesign.
//
// CSS grid restructures the bar into two rows:
//   row 1:  [ scrubber spans available width ]  [ time auto-sizes right ]
//   row 2:  [ buttons distributed across full width                     ]
//
// 44×44 minimum touch targets satisfy Apple HIG. Volume slider popover is
// suppressed — phones have hardware volume buttons; the in-app slider is
// a desktop-only affordance. The volume button still works as a tap-to-
// mute toggle, matching YouTube/Vimeo mobile conventions.
// ───────────────────────────────────────────────────────────────────────────
@media screen and (max-width: 768px) {
  .controls-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "tracks time"
      "controls controls";
    gap: 0.5rem 0.75rem;
    align-items: center;
  }

  .timeline-tracks {
    grid-area: tracks;
    width: 100%;
    gap: 6px;
  }

  .time-display {
    grid-area: time;
    min-width: auto;
    align-self: center;
    white-space: nowrap;
  }

  .timeline-controls {
    grid-area: controls;
    width: 100%;
    justify-content: space-between;
    gap: 0.25rem 0.4rem;
    // Allow wrap so additional slot buttons (download, share) don't overflow
    // narrow phone viewports — they reflow into a second row if needed.
    flex-wrap: wrap;
  }

  // 48×48 play button + 44×44 secondary controls satisfies Apple HIG's
  // touch target minimum without being so large the cluster overflows on
  // narrow phones (~360px wide).
  .play-btn {
    width: 48px !important;
    height: 48px !important;
    flex-shrink: 0;
  }

  .volume-btn,
  .speed-btn,
  .overlay-toggle-btn,
  :slotted(.overlay-toggle-btn) {
    min-width: 44px;
    min-height: 44px;
    padding: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  // The desktop slider is vertical (writing-mode: vertical-lr, 4×80px). On
  // mobile that combination renders unreliably across browsers — Mobile
  // Safari especially mangles custom-styled vertical range inputs, leaving
  // the slider as a barely-visible sliver. Switching to a horizontal slider
  // sidesteps the cross-browser fragility entirely and is more thumb-
  // friendly for adjusting along the slider's length.
  .volume-popover {
    padding: 14px 12px;
    bottom: calc(100% + 6px);
    // Make sure the popover sits above any video poster underneath it.
    z-index: 5;
  }
  .volume-slider {
    // Reset the vertical-mode rules. Range inputs default to horizontal,
    // so unsetting these is enough.
    writing-mode: horizontal-tb;
    direction: ltr;
    width: 140px;
    height: 8px;
    border-radius: 4px;

    &::-webkit-slider-thumb {
      width: 20px;
      height: 20px;
    }
    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
    }
  }

  // Fatter scrubber + visible playhead dot. The visible track grows with
  // it so the fill stays proportional.
  .timeline-track {
    height: 14px;
  }
  .track-fill {
    border-radius: 7px;
  }
  .playhead {
    width: 18px;
    height: 18px;
    top: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  // Speed popover stays available — speed control is genuinely useful on
  // mobile (1.5x for long-form video). Just constrain it from running off
  // the right edge of narrow phones.
  .speed-popover {
    bottom: calc(100% + 4px);
    max-width: calc(100vw - 1rem);
  }

  // No hover on touch — kill the scrubber's hover-time tooltip.
  .hover-tooltip {
    display: none;
  }
}
</style>
